import React, { useMemo, useState } from "react";
import { Link2, Copy, Check, X, Twitter, Linkedin, AlertTriangle, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { postJSON } from "../lib/api";

const AUTH_GATED_PREFIX = ["list_my_", "search_my_", "get_my_", "acknowledge_cve_alert"];

function isAuthGatedToolName(name) {
    if (!name) return false;
    return AUTH_GATED_PREFIX.some((p) => name === p || name.startsWith(p));
}

/**
 * <ShareAnswerModal />
 *
 * Wraps a chat assistant message and lets the signed-in user publish it as a
 * public, indexable /share/{id} URL.
 *
 * Props:
 *   open               — boolean
 *   onClose            — () => void
 *   message            — the assistant message {id, content, toolEvents[]}
 *   userPrompt         — the original user prompt that produced this assistant turn
 */
export const ShareAnswerModal = ({ open, onClose, message, userPrompt }) => {
    const [creating, setCreating] = useState(false);
    const [share, setShare] = useState(null); // {share_id, share_url}
    const [copied, setCopied] = useState(false);
    const [error, setError] = useState(null);

    const toolEvents = useMemo(() => message?.toolEvents || [], [message]);
    const offender = useMemo(() => {
        for (const ev of toolEvents) {
            if (isAuthGatedToolName(ev.name)) return ev.name;
            // also block error states that came back as AUTH_REQUIRED / UPGRADE_REQUIRED
            if (ev.errorCode === "AUTH_REQUIRED" || ev.errorCode === "UPGRADE_REQUIRED") {
                return ev.name || "auth-gated";
            }
        }
        return null;
    }, [toolEvents]);

    const canShare = !offender && toolEvents.length > 0;

    const handleGenerate = async () => {
        setError(null);
        setCreating(true);
        try {
            const payload = {
                user_prompt: userPrompt || "",
                assistant_reply: message?.content || "",
                tool_calls: toolEvents.map((ev) => ({
                    tool_call_id: ev.id,
                    name: ev.name,
                    args: ev.args || {},
                    success: ev.status === "success",
                    result: ev.result || null,
                    error: ev.error || null,
                    error_code: ev.errorCode || null,
                    duration_ms: typeof ev.durationMs === "number" ? ev.durationMs : null,
                })),
                language: "en",
            };
            const r = await postJSON("/chat/share", payload);
            const absoluteUrl = `${window.location.origin}${r.share_url}`;
            setShare({ share_id: r.share_id, share_url: absoluteUrl });
        } catch (e) {
            const detail = e?.response?.data?.detail;
            const msg =
                typeof detail === "object"
                    ? detail.message || "Could not create share."
                    : detail || e?.message || "Could not create share.";
            setError(msg);
        } finally {
            setCreating(false);
        }
    };

    const handleCopy = async () => {
        if (!share?.share_url) return;
        try {
            await navigator.clipboard.writeText(share.share_url);
            setCopied(true);
            toast.success("Link copied to clipboard");
            window.setTimeout(() => setCopied(false), 2500);
        } catch {
            toast.error("Could not copy — select the link manually");
        }
    };

    const handleClose = () => {
        setShare(null);
        setError(null);
        setCopied(false);
        onClose?.();
    };

    const tweetText = encodeURIComponent(
        `${userPrompt ? `"${userPrompt}" → ` : ""}answered by the @CybersecForefront AI co-pilot:`,
    );
    const tweetUrl = share?.share_url ? encodeURIComponent(share.share_url) : "";
    const twitterShare = share
        ? `https://twitter.com/intent/tweet?text=${tweetText}&url=${tweetUrl}`
        : "#";
    const linkedinShare = share
        ? `https://www.linkedin.com/sharing/share-offsite/?url=${tweetUrl}`
        : "#";

    return (
        <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
            <DialogContent
                className="bg-surface border-white/10 text-text-primary max-w-lg"
                data-testid="share-answer-modal"
            >
                <DialogHeader>
                    <DialogTitle className="font-display flex items-center gap-2">
                        <Link2 className="w-4 h-4 text-accent-cyan" />
                        Share this answer publicly
                    </DialogTitle>
                    <DialogDescription className="text-text-muted">
                        Anyone with the link will see the prompt, the tool results, and the
                        AI answer. They will NOT see your account, your IPAM data, or any
                        client information.
                    </DialogDescription>
                </DialogHeader>

                {/* Compact preview of what will be shared */}
                <div className="rounded-lg border border-white/[0.06] bg-elevated/40 p-3 max-h-72 overflow-auto space-y-2">
                    <div>
                        <p className="text-[10.5px] font-mono uppercase tracking-wider text-text-dim">
                            Prompt
                        </p>
                        <p className="text-[13px] text-text-primary mt-0.5 line-clamp-2">
                            {userPrompt || "(no prompt captured)"}
                        </p>
                    </div>
                    <div>
                        <p className="text-[10.5px] font-mono uppercase tracking-wider text-text-dim">
                            Tools invoked ({toolEvents.length})
                        </p>
                        <ul className="text-[12px] font-mono text-text-muted mt-0.5 space-y-0.5">
                            {toolEvents.map((ev) => (
                                <li
                                    key={ev.id}
                                    className="flex items-center gap-2"
                                    data-testid={`share-preview-tool-${ev.name}`}
                                >
                                    <span
                                        className={`inline-block h-1.5 w-1.5 rounded-full ${
                                            isAuthGatedToolName(ev.name)
                                                ? "bg-accent-red"
                                                : "bg-accent-mint"
                                        }`}
                                    />
                                    <span>{ev.name}</span>
                                    {isAuthGatedToolName(ev.name) && (
                                        <span className="text-accent-red">(private)</span>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>
                    {message?.content && (
                        <div>
                            <p className="text-[10.5px] font-mono uppercase tracking-wider text-text-dim">
                                Answer
                            </p>
                            <p className="text-[12.5px] text-text-primary mt-0.5 line-clamp-4 whitespace-pre-wrap">
                                {message.content}
                            </p>
                        </div>
                    )}
                </div>

                {/* Auth-gated tool block */}
                {!canShare && (
                    <div
                        className="rounded-md border border-accent-amber/30 bg-accent-amber/[0.05] p-3 flex items-start gap-2"
                        data-testid="share-block-auth-gated"
                    >
                        <AlertTriangle className="w-4 h-4 text-accent-amber shrink-0 mt-0.5" />
                        <p className="text-[12.5px] text-text-primary leading-relaxed">
                            This message contains data from your private inventory (
                            <span className="font-mono">{offender}</span>). Only answers
                            using public tools (subnet, DNS, CVE lookup) can be shared.
                        </p>
                    </div>
                )}

                {/* Server error */}
                {error && (
                    <div
                        className="rounded-md border border-accent-red/40 bg-accent-red/[0.05] p-3 text-[12.5px] text-accent-red"
                        data-testid="share-error"
                    >
                        {error}
                    </div>
                )}

                {/* Generated link */}
                {share && (
                    <div className="space-y-2" data-testid="share-link-row">
                        <p className="text-[10.5px] font-mono uppercase tracking-wider text-text-dim">
                            Public link
                        </p>
                        <div className="flex items-stretch gap-2">
                            <input
                                readOnly
                                value={share.share_url}
                                className="flex-1 rounded-md border border-white/10 bg-canvas px-3 py-2 text-[12.5px] font-mono text-accent-cyan focus:outline-none focus:ring-1 focus:ring-accent-cyan"
                                data-testid="share-link-input"
                                onFocus={(e) => e.target.select()}
                            />
                            <Button
                                onClick={handleCopy}
                                variant="outline"
                                className="border-white/10 hover:border-accent-cyan/40 hover:text-accent-cyan"
                                data-testid="share-copy-btn"
                            >
                                {copied ? (
                                    <Check className="w-4 h-4" />
                                ) : (
                                    <Copy className="w-4 h-4" />
                                )}
                            </Button>
                        </div>
                        <div className="flex items-center gap-2 pt-1">
                            <a
                                href={twitterShare}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-white/10 text-[12px] text-text-primary hover:border-accent-cyan/40 hover:text-accent-cyan transition-colors"
                                data-testid="share-twitter-btn"
                            >
                                <Twitter className="w-3.5 h-3.5" /> Share on X
                            </a>
                            <a
                                href={linkedinShare}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-white/10 text-[12px] text-text-primary hover:border-accent-cyan/40 hover:text-accent-cyan transition-colors"
                                data-testid="share-linkedin-btn"
                            >
                                <Linkedin className="w-3.5 h-3.5" /> Share on LinkedIn
                            </a>
                            <a
                                href={share.share_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-white/10 text-[12px] text-text-primary hover:border-accent-cyan/40 hover:text-accent-cyan transition-colors"
                                data-testid="share-open-btn"
                            >
                                Open <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                        </div>
                    </div>
                )}

                <DialogFooter className="gap-2">
                    {!share ? (
                        <>
                            <Button
                                variant="ghost"
                                onClick={handleClose}
                                data-testid="share-cancel-btn"
                                className="text-text-muted hover:text-text-primary"
                            >
                                <X className="w-4 h-4 mr-1" /> Cancel
                            </Button>
                            <Button
                                onClick={handleGenerate}
                                disabled={!canShare || creating}
                                className="bg-accent-cyan text-canvas hover:bg-accent-cyan/90 disabled:opacity-50"
                                data-testid="share-generate-btn"
                            >
                                {creating ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                        Generating…
                                    </>
                                ) : (
                                    <>
                                        <Link2 className="w-4 h-4 mr-1" /> Generate link
                                    </>
                                )}
                            </Button>
                        </>
                    ) : (
                        <Button
                            onClick={handleClose}
                            className="bg-accent-cyan text-canvas hover:bg-accent-cyan/90"
                            data-testid="share-done-btn"
                        >
                            Done
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default ShareAnswerModal;
